import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import styles from './ReviewCard.module.scss'; // Adjust to your styling needs
import ConfirmModal from '@/components/atoms/ActionModal';
import StarsPercent from '@/components/atoms/StarsPercent';
import moment from 'moment';
import ProductImageModal from '@/components/organisms/ProductImageModal';

interface ReviewCardProps {
	id?: number;
	rating?: number;
	reviewText?: string;
	isEditable?: boolean;
	images?: any[];
	userName?: string;
	createdDate?: string;
	status?: string;
	item?: any;
	onEdit?: (id) => void;
	onDelete?: (id) => void;
	isDetails?: boolean;
	itemIndex?: number;
	setSelectedItem?: (item: any) => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
	id,
	rating,
	reviewText,
	images,
	userName,
	createdDate,
	status,
	item,
	onEdit,
	onDelete,
	isEditable = true,
	isDetails = false,
	setSelectedItem
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const [previewImage, setPreviewImage] = useState(false);

	// Close the menu if the user clicks outside of it
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsMenuOpen(false);
			}
		};

		// Add event listener when the menu is open
		if (isMenuOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		// Cleanup event listener
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isMenuOpen]);

	const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

	const handleDeleteClick = () => {
		setIsModalOpen(true);
	};

	return (
		<div className={styles.reviewInfo}>
			{/* Review Actions */}
			<div className={styles.reviewAction}>
				<StarsPercent rating={rating} size='20' />
				{isEditable && (
					<HiOutlineDotsVertical className={styles.actionIcon} width={24} height={24} onClick={toggleMenu} />
				)}

				{isDetails && (
					<>
						<p className={styles.user}>{userName}</p>
						<p className={styles.date}>{moment(createdDate).fromNow()}</p>
					</>
				)}

				{/* Menu */}
				{isMenuOpen && (
					<div ref={menuRef} className={styles.menu}>
						<button
							className={styles.menuItem}
							onClick={() => {
								onEdit(id && id);
								setSelectedItem(item);
							}}>
							Edit
						</button>

						<button className={styles.menuItem} onClick={handleDeleteClick}>
							Delete
						</button>
					</div>
				)}

				{/* Confirmation Modal */}
				{isModalOpen && (
					<ConfirmModal
						type='danger'
						title={'Are you sure?'}
						cancelText='No'
						confirmText='Delete'
						message={'Do you want to delete this review?'}
						onConfirm={() => {
							onDelete(id && id);
							setIsModalOpen(false);
						}}
						onCancel={() => setIsModalOpen(false)}
						isVisible={isModalOpen}
					/>
				)}
			</div>

			{/* Review Text */}
			{reviewText && <p className={styles.reviewText}>{reviewText}</p>}

			{/* Images */}
			{images && images.length > 0 && (
				<div className={styles.images}>
					{images?.map((image, index) => (
						<Image
							key={index}
							onClick={() => setPreviewImage(true)}
							src={image?.src}
							alt={'Reviewed-' + index}
							className={styles.image}
							objectFit='cover'
							width={64}
							height={64}
							unoptimized={true}
							style={{ borderRadius: 10, border: '1px solid #EEEFF2' }}
						/>
					))}
				</div>
			)}

			{/* Review Status */}
			{status && status !== 'approved' && <p className={styles.reviewStatus}>{'Pending Approval'}</p>}

			{previewImage && (
				<ProductImageModal
					images={images}
					isOpen={previewImage}
					onClose={() => setPreviewImage(false)}
					onConfirm={() => setPreviewImage(false)}
					isTitle={false}
				/>
			)}
		</div>
	);
};

export default ReviewCard;
